<template>
  <vx-card title="Procesos por resultados" id="card">
    <!-- LOADING -->
    <div
      v-if="uiState === 'loading'"
      class="gw-empty-state"
      style="height:350px;"
    >
      <div class="gw-spinner" aria-label="Cargando"></div>
      <div class="gw-empty-title">Cargando información…</div>
      <div class="gw-empty-subtitle">Un momento por favor</div>
    </div>

    <!-- READY (CHART) -->
    <VueApexCharts
      v-else-if="uiState === 'ready' && renderGraphic"
      type="donut"
      height="350"
      :options="chartOptions"
      :series="series"
    />

    <!-- EMPTY / ERROR -->
    <div
      v-else
      class="gw-empty-state"
      style="height:350px;"
    >
      <img
        class="gw-empty-img"
        :src="emptyStateImage"
        :alt="emptyStateAlt"
      />
      <div class="gw-empty-title">{{ emptyStateTitle }}</div>
      <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
    </div>
  </vx-card>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,

      requestKey: null,
      consultHandler: null,

      series: [],

      // Empty/Error illustrations (S3)
      IMG_EMPTY: publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR: publicAssetUrl('Assets/illustrations/error.png'),

      chartOptions: {
        labels: ["Confiables", "Confiables con observaciones", "No confiables"],
        colors: ["#5F0091", "#ffc107", "#dc3545"],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { width: 200 },
              legend: { position: "bottom" },
            },
          },
        ],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(2) + "%";
          },
        },
      },
    };
  },

  components: {
    VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },

    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },

    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },

    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      var path =
        "/metrics/projects-by-results?client_id=" +
        this.userInfo.user.client_id;

      var url = "";

      if (this.startDate != null && this.endDate != null) {
        var params =
          "&start_date=" + this.startDate + "&end_date=" + this.endDate;
        url = this.url() + path + params;
      } else {
        url = this.url() + path;
      }

      if (this.cityId != null) {
        url += "&city_id=" + this.cityId;
      }

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    setReady(series) {
      // Forzar remount para evitar glitches de ApexCharts
      this.renderGraphic = false;
      this.series = series;
      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      var key =
        (this.startDate || "") +
        "|" +
        (this.endDate || "") +
        "|" +
        (this.cityId || "");

      this.setLoading(key);

      console.log("[projects-by-results] initializeData:", {
        origin: origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        var request = await this.requestGetWithoutContent(this.getUrl(), {});

        // Evitar race conditions
        if (this.requestKey !== key) {
          console.log("[projects-by-results] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn(
            "[projects-by-results] request not ok:",
            request && request.status
          );
          this.setError();
          return;
        }

        var data = request.data;

        // Validación de estructura
        if (!Array.isArray(data) || data.length === 0 || !data[0]) {
          console.warn("[projects-by-results] Empty data. Showing empty state.");
          this.setEmpty();
          return;
        }

        this.applyDataToSeries(data[0]);
      } catch (e) {
        // Evitar race conditions
        if (this.requestKey !== key) return;

        console.warn("[projects-by-results] request exception:", e);
        this.setError();
      }
    },

    applyDataToSeries(row) {
      // Asegurar números
      var trust = row.trustprojects;
      var unreliable = row.unreliableprojects;
      var untrust = row.untrusprojects;

      if (typeof trust === "string") trust = parseFloat(trust);
      if (typeof unreliable === "string") unreliable = parseFloat(unreliable);
      if (typeof untrust === "string") untrust = parseFloat(untrust);

      trust = typeof trust === "number" && !isNaN(trust) ? trust : 0;
      unreliable = typeof unreliable === "number" && !isNaN(unreliable) ? unreliable : 0;
      untrust = typeof untrust === "number" && !isNaN(untrust) ? untrust : 0;

      var total = trust + unreliable + untrust;

      // Si todo es 0, mostrar "sin datos"
      if (!total || total <= 0) {
        console.warn("[projects-by-results] All zeros. Showing empty state.");
        this.setEmpty();
        return;
      }

      // Ojo: labels son [Confiables, Confiables con obs, No confiables]
      var series = [trust, unreliable, untrust];

      console.log("[projects-by-results] series final:", series);
      this.setReady(series);
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () => this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

/* Simple loader */
.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
